/**
 * Client-side mirror of the auth session.
 *
 * Access and refresh tokens live in httpOnly cookies, so the app cannot read
 * them to answer "is this user signed in?" before its first API call. This flag
 * is that answer: written when a session starts, cleared the moment one
 * definitively ends (explicit logout, or a refresh token the server rejects).
 *
 * It is a hint, never an authority — the cookies stay the source of truth for
 * the backend. Its only job is to tell the app whether verifying is worthwhile,
 * so a signed-in user hitting /login is recognised and redirected, while a
 * genuine visitor costs zero API calls.
 *
 * Every access is wrapped: localStorage throws outright in some privacy modes.
 */

const SESSION_KEY = 'hasSession';
const CLINIC_KEY = 'selectedClinicId';

export const markSessionStarted = (): void => {
  try {
    localStorage.setItem(SESSION_KEY, '1');
  } catch {
    /* storage unavailable — the probe just won't run next load */
  }
};

export const hasStoredSession = (): boolean => {
  try {
    if (localStorage.getItem(SESSION_KEY) === '1') return true;
    // Back-compat: sessions started before this flag existed left only an clinic
    // id behind. Treat that as a session so users already signed in when this
    // shipped aren't stuck seeing /login until their next manual sign-in.
    return !!localStorage.getItem(CLINIC_KEY);
  } catch {
    return false;
  }
};

/**
 * Drop the flag and every other auth-scoped key. Safe to call repeatedly, and
 * safe to call while already on a public page — clearing is what stops a dead
 * session from re-running the probe on every single page load.
 */
export const clearSessionStorage = (): void => {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem('rememberMe');
    localStorage.removeItem(CLINIC_KEY);
    // Per-clinic branch selections (selectedBranchId_<clinicId>)
    Object.keys(localStorage)
      .filter((k) => k.startsWith('selectedBranchId_'))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    /* nothing to clear if storage is unavailable */
  }
};

/**
 * Where a signed-out (or clinic-less) visitor was going when the route guard
 * sent them to sign in or pick a clinic. Login and clinic selection used to
 * land everyone on Home, so a link from an email or a bookmark lost its
 * target. Only in-app dashboard paths and patient QR links are kept, which
 * rules out redirecting anywhere off the app.
 */
const RETURN_TO_KEY = 'returnTo';

const canReturnTo = (path: string): boolean =>
  path.startsWith('/dashboard/') || path.startsWith('/p/');

export const rememberReturnTo = (path: string): void => {
  if (!canReturnTo(path)) return;
  try {
    sessionStorage.setItem(RETURN_TO_KEY, path);
  } catch {
    /* storage unavailable: the user lands on Home as before */
  }
};

/** The remembered path (once), else the dashboard root. */
export const consumeReturnTo = (): string => {
  try {
    const path = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
    if (path && canReturnTo(path)) return path;
  } catch {
    /* fall through */
  }
  return '/dashboard';
};
